import { widenLadder } from './cuisine';
import { isMealPlaceType, mealQuery } from './meal';
import { ALL_PRICE_LEVELS, effectiveRadius, withFacts, type Facts } from './facts';
import type { LatLng, PlaceResult, PriceLevel, SearchRequest } from './types';

/**
 * Human wording for every type the two trees and the pair table can produce.
 *
 * `tests/mapping.test.ts` pins this both ways: a type with no label here, or a label for
 * a type nothing can reach, is a test failure rather than something that shows up as
 * `chinese_noodle_restaurant` in a sentence on the results screen.
 */
const TYPE_LABELS: Record<string, string> = {
  // Region nodes - what a descent that stops partway down ends up with.
  asian_restaurant: 'asian food',
  european_restaurant: 'european food',
  mediterranean_restaurant: 'mediterranean food',
  middle_eastern_restaurant: 'middle eastern food',
  latin_american_restaurant: 'latin american food',
  south_american_restaurant: 'south american food',
  caribbean_restaurant: 'caribbean food',
  african_restaurant: 'african food',

  // Asia
  japanese_restaurant: 'japanese food',
  sushi_restaurant: 'sushi',
  ramen_restaurant: 'ramen',
  chinese_restaurant: 'chinese food',
  chinese_noodle_restaurant: 'chinese noodles',
  dim_sum_restaurant: 'dim sum',
  korean_restaurant: 'korean food',
  korean_barbecue_restaurant: 'korean barbecue',
  taiwanese_restaurant: 'taiwanese food',
  indian_restaurant: 'indian food',
  south_indian_restaurant: 'south indian food',
  north_indian_restaurant: 'north indian food',
  pakistani_restaurant: 'pakistani food',
  thai_restaurant: 'thai food',
  vietnamese_restaurant: 'vietnamese food',
  filipino_restaurant: 'filipino food',
  indonesian_restaurant: 'indonesian food',

  // The Middle East
  lebanese_restaurant: 'lebanese food',
  turkish_restaurant: 'turkish food',
  persian_restaurant: 'persian food',
  shawarma_restaurant: 'shawarma',
  falafel_restaurant: 'falafel',
  kebab_shop: 'kebabs',

  // Europe
  italian_restaurant: 'italian food',
  pizza_restaurant: 'pizza',
  greek_restaurant: 'greek food',
  gyro_restaurant: 'gyros',
  spanish_restaurant: 'spanish food',
  tapas_restaurant: 'tapas',
  portuguese_restaurant: 'portuguese food',
  french_restaurant: 'french food',
  british_restaurant: 'british food',
  fish_and_chips_restaurant: 'fish and chips',
  german_restaurant: 'german food',

  // The Americas
  mexican_restaurant: 'mexican food',
  taco_restaurant: 'tacos',
  american_restaurant: 'american food',
  hamburger_restaurant: 'burgers',
  steak_house: 'steak',
  barbecue_restaurant: 'barbecue',
  cuban_restaurant: 'cuban food',
  brazilian_restaurant: 'brazilian food',
  peruvian_restaurant: 'peruvian food',
  argentinian_restaurant: 'argentinian food',

  // Africa
  moroccan_restaurant: 'moroccan food',
  ethiopian_restaurant: 'ethiopian food',

  // Dish-tree leaves that no tradition sharpens on its own.
  noodle_shop: 'noodles',
  dumpling_restaurant: 'dumplings',
  hot_pot_restaurant: 'hot pot',
  sandwich_shop: 'sandwiches',
  chicken_restaurant: 'chicken',
  seafood_restaurant: 'seafood',
  salad_shop: 'salads',

  // Set by the meal shortcut at breakfast and brunch hours, not by either food tree.
  breakfast_restaurant: 'breakfast',
  brunch_restaurant: 'brunch',

  // Set by the style axis rather than either food tree. Nothing sets a fine-dining
  // type: "special occasion?" moves the price levels instead, which narrows results
  // without risking an empty page in a thin area.
  fast_food_restaurant: 'fast food',
};

export function typeLabel(type: string | undefined): string | undefined {
  if (!type) return undefined;
  return TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

/**
 * Types every eating place carries, which say nothing about what it serves.
 * `restaurant` is the last-resort fallback below rather than a category in its own right.
 */
const GENERIC_TYPES = new Set([
  'restaurant',
  'food',
  'store',
  'point_of_interest',
  'establishment',
  'meal_takeaway',
  'meal_delivery',
]);

/**
 * What Google calls this place, for the result card.
 *
 * `primaryTypeDisplayName` is already human text ("Italian restaurant") and is what
 * Maps itself shows, so it wins when present. It is absent often enough - Google omits
 * it for places it hasn't classified - that the raw `types` list is worth falling back
 * to, taking the first entry that isn't true of every restaurant. Deliberately not
 * routed through `typeLabel`: that maps a *search* type to how a question phrased it
 * ("italian food"), which is a different sentence than a label on a card.
 */
export function placeCategory(
  place: Pick<PlaceResult, 'primaryType' | 'types'>,
): string | undefined {
  const raw =
    place.primaryType ??
    place.types.find((type) => !GENERIC_TYPES.has(type)) ??
    place.types.find((type) => type === 'restaurant');
  if (!raw) return undefined;

  const words = raw.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function priceWord(levels: PriceLevel[] | undefined): string | null {
  if (!levels || levels.length === 0 || levels.length === ALL_PRICE_LEVELS.length) {
    return null;
  }
  if (levels.includes('PRICE_LEVEL_VERY_EXPENSIVE')) return 'upscale';
  if (levels.includes('PRICE_LEVEL_INEXPENSIVE')) return 'cheap';
  return null;
}

/**
 * Text Search (New) requires a non-empty `textQuery`, so we build a natural phrase.
 * Structured filters still do the real narrowing - the phrase mostly steers relevance
 * ranking, which is why it stays short and free of negations (Google does not honour
 * "not italian", and the descent never produces one anyway: every answer moves the
 * user *towards* something).
 */
export function buildTextQuery(facts: Facts): string {
  const parts: string[] = [];

  const price = priceWord(facts.priceLevels);
  if (price) parts.push(price);

  // Ahead of the cuisine word so the phrase reads naturally ("vegetarian thai food").
  if (facts.vegetarian) parts.push('vegetarian');

  const cuisine = typeLabel(facts.includedType);
  // The shortcut already asked for the meal as a place, so repeating it would give
  // "breakfast for breakfast or brunch".
  const meal =
    facts.meal && !isMealPlaceType(facts.includedType) ? mealQuery(facts.meal) : null;

  if (cuisine) {
    parts.push(cuisine);
    // Trails the cuisine with a connector rather than stacking another adjective in
    // front of it: "thai food for lunch" is a phrase, "lunch thai food" is a pile.
    if (meal) parts.push(`for ${meal}`);
  } else if (meal) {
    // With no cuisine to narrow things, the meal is the most specific thing the run
    // knows - a better query than the bare "restaurants" fallback below.
    parts.push(meal);
  } else if (facts.sitDown === true) {
    parts.push('sit down restaurants');
  } else if (facts.sitDown === false) {
    parts.push('quick food');
  } else {
    parts.push('restaurants');
  }

  return parts.join(' ');
}

/**
 * The same run, asking for one step less than it settled on.
 *
 * An advanced descent can finish on something genuinely rare - shawarma, tapas, gyros -
 * and outside a big city that returns nothing at all. Climbing one rung back up the
 * descent is a better answer than an empty screen, and it is the *only* thing widened:
 * price, radius and opening hours are what the user actually asked for, so relaxing
 * those would be answering a different question.
 *
 * Returns null when there is nothing broader to ask for - the run either never chose a
 * cuisine, or is already at the top of its branch.
 */
export function widenFacts(facts: Facts): Facts | null {
  if (!facts.includedType) return null;

  const ladder = widenLadder(facts);
  const index = ladder.lastIndexOf(facts.includedType);

  // Not on the ladder at all means the style axis set it (`fast_food_restaurant`);
  // dropping the type entirely is the honest widening for that.
  const wider = index > 0 ? ladder[index - 1] : undefined;
  if (wider === facts.includedType) return null;

  return withFacts(facts, { includedType: wider });
}

/** The one place an answer becomes an API parameter. */
export function toSearchRequest(facts: Facts, center: LatLng): SearchRequest {
  const radiusM = effectiveRadius(facts);

  const request: SearchRequest = {
    textQuery: buildTextQuery(facts),
    center,
    radiusM,
    // Walking distance means proximity beats reputation; otherwise let Google rank.
    rankPreference: radiusM <= 1500 ? 'DISTANCE' : 'RELEVANCE',
  };

  if (facts.includedType) {
    request.includedType = facts.includedType;
  }

  // Sending every level is the same as sending none, and omitting it keeps the
  // request smaller and the cache key stabler.
  if (facts.priceLevels && facts.priceLevels.length < ALL_PRICE_LEVELS.length) {
    request.priceLevels = facts.priceLevels;
  }

  if (facts.openNow !== undefined) {
    request.openNow = facts.openNow;
  }

  if (facts.minRating !== undefined) {
    request.minRating = facts.minRating;
  }

  return request;
}
